/** Bounded Canvas resources; device hints are optional, measured load can only lower quality. */
export type ParticleQuality = 'low' | 'balanced' | 'high';

interface DeviceHints {
  deviceMemory?: number;
  hardwareConcurrency?: number;
  saveData?: boolean;
}

export const PARTICLE_QUALITY = {
  low: { fps: 30, maxDpr: 1, maxPixels: 900_000, density: 0.8, halo: false },
  balanced: { fps: 45, maxDpr: 1.5, maxPixels: 1_800_000, density: 0.9, halo: true },
  high: { fps: 60, maxDpr: 2, maxPixels: 2_500_000, density: 1, halo: true },
} as const;

export function initialParticleQuality(hints: DeviceHints): ParticleQuality {
  const memory = hints.deviceMemory;
  const cores = hints.hardwareConcurrency;
  if (
    hints.saveData ||
    (memory !== undefined && memory <= 2) ||
    (cores !== undefined && cores <= 2)
  ) {
    return 'low';
  }
  if ((memory !== undefined && memory <= 4) || (cores !== undefined && cores <= 4)) {
    return 'balanced';
  }
  if (memory !== undefined && memory >= 8 && (cores === undefined || cores >= 6)) {
    return 'high';
  }
  return 'balanced';
}

export function lowerParticleQuality(quality: ParticleQuality): ParticleQuality {
  return quality === 'high' ? 'balanced' : 'low';
}

/** A pixel cap also applies below DPR 1 on very large screens. */
export function particleResolution(
  width: number,
  height: number,
  devicePixelRatio: number,
  quality: ParticleQuality,
): { width: number; height: number; scaleX: number; scaleY: number } {
  const cssWidth = Number.isFinite(width) ? Math.max(1, width) : 1;
  const cssHeight = Number.isFinite(height) ? Math.max(1, height) : 1;
  const nativeDpr =
    Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const budget = PARTICLE_QUALITY[quality];
  const scale = Math.min(
    nativeDpr,
    budget.maxDpr,
    Math.sqrt(budget.maxPixels / cssWidth / cssHeight),
  );
  const pixelWidth = Math.max(1, Math.floor(cssWidth * scale));
  // Also bound degenerate, extremely wide/tall layouts after the minimum 1px clamp.
  const pixelHeight = Math.max(
    1,
    Math.min(Math.floor(cssHeight * scale), Math.floor(budget.maxPixels / pixelWidth)),
  );
  const boundedWidth = Math.min(pixelWidth, budget.maxPixels);
  return {
    width: boundedWidth,
    height: pixelHeight,
    scaleX: boundedWidth / cssWidth,
    scaleY: pixelHeight / cssHeight,
  };
}

interface FrameSample {
  frames: number;
  elapsedMs: number;
  renderTimeMs: number;
  expensiveFrames: number;
  slowFrames: number;
}

/** Ignore isolated stalls and startup/resize; require a sustained sampling window. */
export function shouldReduceParticleQuality(
  quality: ParticleQuality,
  sample: FrameSample,
): boolean {
  if (quality === 'low' || sample.elapsedMs < 2500 || sample.frames < 20) return false;
  const frameBudget = 1000 / PARTICLE_QUALITY[quality].fps;
  return (
    (sample.renderTimeMs / sample.frames > frameBudget * 0.5 &&
      sample.expensiveFrames / sample.frames > 0.3) ||
    sample.slowFrames / sample.frames > 0.65
  );
}
